"use client";

import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Clock, MapPin, PhoneCall, MessageCircle, Flame, Layers, CircleCheck as CheckCircle2, Wrench, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const scopeItems = [{
  icon: Flame,
  title: "تنظيف حجرة الفرن والشوايات والرفوف",
  description: "فك الرفوف والصواني وتنظيفها بتقنية متقدمة مع جلي كامل لقاع وجوانب الحجرة الداخلية لإزالة الدهون المحترقة نهائياً."
}, {
  icon: Layers,
  title: "تلميع وتفكيك زجاج الأبواب",
  description: "تفكيك احترافي لطبقات زجاج الباب المزدوج وإزالة الأبخرة والزيوت المتراكمة بين الألواح وإعادة التركيب بدقة وإحكام."
}, {
  icon: Sparkles,
  title: "إزالة الكربون دون الإضرار بالطلاء",
  description: "مركبات تنظيف متطورة تذيب ترسبات الكربون والشحوم الصعبة دون التأثير على طلاء الفرن الخارجي أو الحساسات الداخلية."
}];
const reasons = [{
  icon: MapPin,
  title: "تواجد محلي في حي الياسمين",
  description: "خبرة معتمدة وسرعة وصول لمنازل وسكان حي الياسمين وشمال الرياض."
}, {
  icon: Clock,
  title: "استجابة سريعة ومواعيد دقيقة",
  description: "التزام كامل بمواعيد الزيارة المنزلية وفريق متأهب لتلبية الحجوزات السريعة."
}, {
  icon: Wrench,
  title: "كوادر فنية مدربة",
  description: "فنيون متمرسون في فك وتنظيف وتجميع أجهزة الأفران بأمان تام واحترافية."
}, {
  icon: ShieldCheck,
  title: "مواد تنظيف آمنة ومعتمدة",
  description: "خامات صديقة لبيئة المطبخ، فعالة ضد الدهون وقاسية على الشحوم ومأمونة على الصحة."
}];
const faqs = [{
  question: "كم مرة يُنصح بتنظيف الفرن تنظيفاً عميقاً؟",
  answer: "نوصي بإجراء تنظيف شامل وعميق كل 3 إلى 6 أشهر وفقاً لكثافة الاستخدام، وذلك للحفاظ على كفاءة التسخين ومنع تراكم الروائح والدهون المشتعلة."
}, {
  question: "كم تستغرق الزيارة المنزلية المعتادة؟",
  answer: "تستغرق الزيارة في العادة من 60 إلى 90 دقيقة بحسب حالة الفرن وحجم التراكمات الدهنية، ويشمل ذلك الفحص والتنظيف والتجفيف والاختبار."
}, {
  question: "هل الخدمة ملائمة لجميع الأفران الغازية والكهربائية؟",
  answer: "نعم، نقدم الخدمة لكافة أنواع وماركات الأفران الغازية والأفران الكهربائية والمدمجة (Built-in)، مع مراعاة كاملة للحساسات والمكونات الإلكترونية."
}, {
  question: "كيف يمكنني حجز موعد الخدمة في حي الياسمين؟",
  answer: "يمكنك الحجز مباشرة عبر التواصل معنا هاتفياً أو إرسال رسالة واتساب إلى الرقم الموحد، وسنقوم بتحديد الموعد المناسب لزيارة الفني إلى باب منزلك فوراً."
}];
export default function OvenCleaningAlYasmin() {
  return <section id="ovencleaningalyasmin" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 sm:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        
        {/* Block 1: Header & Core Service Scope */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="space-y-10">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="w-8 h-1 bg-primary mx-auto rounded-full" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              خدمة تنظيف الأفران بحي الياسمين بالرياض
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              تنظيف عميق ومتكامل لمقصورة الفرن وملحقاته مع إزالة تامة لطبقات الدهون والكربون بأحدث المواد الفعالة والآمنة.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {scopeItems.map((item, index) => {
            const IconComp = item.icon;
            return <Card key={index} data-index={index} className="border border-border/80 shadow-xs hover:shadow-md transition-shadow duration-200 bg-card flex flex-col justify-between">
                  <CardHeader className="space-y-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <IconComp className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </motion.div>

        {/* Block 2: Why Choose لمسة احتراف */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              لماذا تختار شركة لمسة احتراف؟
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              معايير عالية من الدقة والسلامة لضمان نظافة فرنك وعودته كأنه جديد.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return <div key={index} data-index={index} className="p-5 rounded-lg border border-border/60 bg-muted/40 flex flex-col space-y-3">
                  <div className="size-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Icon className="size-5" />
                  </div>
                  <h4 className="text-base font-semibold text-foreground">
                    {reason.title}
                  </h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {reason.description}
                  </p>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Block 3: Service Coverage Panel */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }}>
          <div className="rounded-xl border border-border bg-muted/50 p-6 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-3 text-right max-w-2xl">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <MapPin className="size-4" />
                <span>نطاق التغطية والزيارات المنزلية</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                خدمة سريعة في حي الياسمين وأحياء شمال الرياض المجاورة
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                نصلكم مباشرة إلى باب المنزل بفريق مجهز بالكامل من الأدوات والمواد التخصصية داخل حي الياسمين، مع تغطية مستمرة لكافة الأحياء المجاورة مثل النرجس، الصحافة، العقيق، والملقا.
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              <div className="flex items-center gap-2 bg-background px-4 py-3 rounded-lg border border-border shadow-2xs">
                <CheckCircle2 className="size-5 text-chart-4" />
                <span className="text-sm font-medium">زيارات منزلية مجدولة وفورية</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Block 4: Frequently Asked Questions */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="max-w-3xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold">
              <HelpCircle className="size-4" />
              <span>الأسئلة الشائعة</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              إجابات عن خدمة تنظيف الأفران
            </h3>
          </div>

          <Accordion type="single" collapsible className="w-full border rounded-lg bg-card px-4 border-border divide-y divide-border">
            {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} className="border-b-0 py-1">
                <AccordionTrigger className="text-base font-semibold hover:no-underline text-right py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Block 5: Call to Action Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }}>
          <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 shadow-lg text-center space-y-6 relative overflow-hidden">
            <div className="max-w-2xl mx-auto space-y-3">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                احجز خدمة تنظيف الفرن في حي الياسمين الآن
              </h3>
              <p className="text-secondary-foreground/80 text-sm sm:text-base">
                فريق لمسة احتراف مستعد لتلبية طلبكم وتحديد موعد الزيارة المنزلية المناسب لكم على مدار اليوم.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20ba59] text-white font-medium shadow-sm transition-transform active:scale-95">
                <Link to={WHATSAPP_HREF} newTab className="flex items-center gap-2">
                  <MessageCircle className="size-5" />
                  <span>حجز عبر الواتساب</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 font-medium">
                <a href={PHONE_TEL_HREF} className="flex items-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>اتصال مباشر: {PHONE_LOCAL}</span>
                </a>
              </Button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>;
}